import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UsersIcon } from '@heroicons/react/24/outline';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import BucketManagement from './BucketManagement';
import PermissionManagement from './PermissionManagement';
import BucketTemplateManager from './BucketTemplateManager';
import UserDeleteConfirmation from './UserDeleteConfirmation';
import PaginatedTable from './PaginatedTable';
import ActivityLogsModal from './ActivityLogsModal';
import UserDataUsageModal from './UserDataUsageModal';
import RetentionSettings from './RetentionSettings';
import BrandingSettings from './BrandingSettings';
import PasswordPolicySettings from './PasswordPolicySettings';
import { useCache } from '../hooks/useCache';
import DashboardSwitcher from './DashboardSwitcher';
import MFAStatusBadge from './MFAStatusBadge';
import AdminMFAControls from './AdminMFAControls';
import MFAStatisticsWidget from './MFAStatisticsWidget';

interface User {
  userId: string;
  role: string;
  createdAt: string;
  monthlyDataUsage: number;
  monthlyDownloadSize?: number;
  monthlyUploadSize?: number;
  status: string;
  isEnabled: boolean;
  mfaRequired?: boolean;
  mfaSetupComplete?: boolean;
  mfaRequiredDate?: string;
  mfaSetupDate?: string;
  mfaLastUsed?: string;
  mfaRequiredBy?: string;
}

interface ActivityLog {
  userId: string;
  timestamp: string;
  action: string;
  details: any;
}

interface EnhancedAdminDashboardProps {
  onSwitchToBrowser?: () => void;
  dashboardMode?: 'management' | 'browser';
}

const EnhancedAdminDashboard: React.FC<EnhancedAdminDashboardProps> = ({ 
  onSwitchToBrowser,
  dashboardMode = 'management'
}) => {
  const [activeTab, setActiveTab] = useState('users');
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);

  // User management state
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [bulkImportData, setBulkImportData] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showActivityLogs, setShowActivityLogs] = useState<string | null>(null);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [showDataUsageModal, setShowDataUsageModal] = useState<string | null>(null);

  // Bulk MFA selection state (MFA Management tab)
  const [selectedMfaUsers, setSelectedMfaUsers] = useState<Set<string>>(new Set());
  const [mfaBulkLoading, setMfaBulkLoading] = useState(false);

  // Use cache for users data
  const {
    data: users = [],
    loading: usersLoading,
    refresh: refreshUsers
  } = useCache(
    'admin-users-enhanced',
    async () => {
      const response = await apiCall('/admin/users');
      return Array.isArray(response) ? response : (response?.users ?? []);
    },
    { ttl: 30 * 1000 } // 30 seconds cache for admin data
  );

  // Use cache for external buckets
  const {
    data: externalBucketsData = { buckets: [], summary: { total: 0, available: 0, managed: 0, filtered: 0 } },
    loading: externalBucketsLoading,
    refresh: refreshExternalBuckets
  } = useCache(
    'admin-external-buckets',
    async () => {
      try {
        const response = await apiCall('/admin/external-buckets');
        return response;
      } catch (error) {
        console.error('External buckets API error:', error);
        return { buckets: [], summary: { total: 0, available: 0, managed: 0, filtered: 0 } };
      }
    },
    { ttl: 60 * 1000 } // 1 minute cache
  );

  const externalBuckets = externalBucketsData?.buckets || [];
  const bucketsSummary = externalBucketsData?.summary || { total: 0, available: 0, managed: 0, filtered: 0 };

  // Use cache for audit trail
  const {
    data: auditTrail = [],
    loading: auditTrailLoading,
  } = useCache(
    'admin-audit-trail',
    async () => {
      const response = await apiCall('/admin/audit-trail');
      return Array.isArray(response) ? response : (response?.items ?? response?.entries ?? []);
    },
    { ttl: 30 * 1000 } // 30 seconds cache
  );

  const loadActivityLogs = async (userId: string) => {
    setActivityLogsLoading(true);
    setShowActivityLogs(userId);
    try {
      const response = await apiCall(`/admin/activity-logs/${userId}`);
      setActivityLogs(response);
    } catch (error) {
      toast.error('Failed to load activity logs');
      setShowActivityLogs(null);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const handleBulkImport = async () => {
    try {
      setLoading(true);
      const users = bulkImportData.split('\n').map(line => {
        const [email, role] = line.split(',');
        return { email: email?.trim(), role: role?.trim() || 'user' };
      }).filter(user => user.email);

      const response = await apiCall('/admin/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ users })
      });

      toast.success(`Bulk import completed. ${(response.results || []).filter((r: any) => r.status === 'success').length} users created.`);
      setBulkImportData('');
      setShowBulkImport(false);
      refreshUsers();
    } catch (error) {
      toast.error('Bulk import failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await apiCall(`/admin/user-status/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ isEnabled: !currentStatus })
      });
      toast.success(`User ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      await apiCall(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      toast.success('User role updated successfully');
      setEditingUser(null);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const forcePasswordReset = async (userId: string) => {
    try {
      await apiCall(`/admin/password-reset/${userId}`, { method: 'POST' });
      toast.success('Password reset initiated. User will receive reset instructions via email.');
      refreshUsers(); // Refresh to show updated status
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      await apiCall(`/admin/users/${deletingUser.userId}`, {
        method: 'DELETE'
      });
      toast.success('User deleted successfully');
      setDeletingUser(null);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to delete user');
      setDeletingUser(null);
    }
  };

  // Apply an MFA requirement change to every currently selected user
  const handleBulkMFA = async (require: boolean) => {
    const userIds = Array.from(selectedMfaUsers);
    if (userIds.length === 0) return;

    const action = require ? 'Require' : 'Remove the requirement for';
    if (!window.confirm(
      `${action} MFA for ${userIds.length} user${userIds.length !== 1 ? 's' : ''}?\n\n` +
      (require
        ? 'Users who have not set up MFA will be prompted on their next login.'
        : 'These users will no longer need to enter a code when logging in.')
    )) {
      return;
    }

    try {
      setMfaBulkLoading(true);
      const endpoint = require ? '/admin/mfa/require-bulk' : '/admin/mfa/unrequire-bulk';
      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({ userIds })
      });

      const { successful = 0, skipped = 0, failed = 0 } = response.summary || {};

      if (successful > 0) {
        toast.success(`MFA updated for ${successful} user${successful !== 1 ? 's' : ''}`);
      }
      if (skipped > 0) {
        toast(`${skipped} user${skipped !== 1 ? 's were' : ' was'} already in that state`, { icon: 'ℹ️' });
      }
      if (failed > 0) {
        const firstError = (response.results || []).find((r: any) => r.status === 'failed');
        toast.error(
          `${failed} user${failed !== 1 ? 's' : ''} could not be updated` +
          (firstError?.error ? `: ${firstError.error}` : '')
        );
      }

      setSelectedMfaUsers(new Set());
      refreshUsers();
    } catch (error: any) {
      console.error('Bulk MFA update failed:', error);
      toast.error(error?.message || 'Failed to update MFA for selected users');
    } finally {
      setMfaBulkLoading(false);
    }
  };

  const importExternalBucket = async (bucketName: string) => {
    try {
      await apiCall('/admin/external-buckets', {
        method: 'POST',
        body: JSON.stringify({ bucketName, description: 'Imported external bucket' })
      });

      toast.success(`Bucket "${bucketName}" imported successfully with CORS configured`);
      refreshExternalBuckets();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Failed to import bucket "${bucketName}". Check if you have permissions and the bucket exists.`);
    }
  };

  return (
    <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          {onSwitchToBrowser && (
            <DashboardSwitcher
              currentMode={dashboardMode}
              userRole="admin"
              onSwitchToBrowser={onSwitchToBrowser}
            />
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex justify-between">
            {[
              { id: 'users', name: 'User Management' },
              { id: 'buckets', name: 'Bucket Management' },
              { id: 'permissions', name: 'Permission Management' },
              { id: 'mfa', name: 'MFA Management' },
              { id: 'password-policy', name: 'Password Policy' },
              { id: 'templates', name: 'Bucket Templates' },
              { id: 'external', name: 'External Buckets' },
              { id: 'audit', name: 'Audit Trail' },
              { id: 'retention', name: 'Data Retention' },
              { id: 'branding', name: 'Branding' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">User Management</h2>
              <button
                onClick={() => setShowBulkImport(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Bulk Import Users
              </button>
            </div>

            {/* Bulk Import Modal */}
            {showBulkImport && ReactDOM.createPortal(
              <AnimatePresence>
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <motion.div
                    key="bulk-import-modal"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden"
                  >
                  <div className="overflow-y-auto max-h-[90vh] p-8">
                  <div className="text-center mb-6">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                      <UsersIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Bulk Import Users</h3>
                    <p className="text-sm text-gray-600 mt-1">One entry per line: email,role</p>
                  </div>
                  <div className="space-y-1 mb-4">
                    <label htmlFor="admin-bulk-import-data" className="block text-sm font-medium text-gray-700">
                      User list
                    </label>
                    <textarea
                      id="admin-bulk-import-data"
                      value={bulkImportData}
                      onChange={(e) => setBulkImportData(e.target.value)}
                      placeholder="email1@example.com,user&#10;email2@example.com,manager&#10;email3@example.com,admin"
                      className="w-full h-32 mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: one <code>email,role</code> pair per line.</p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowBulkImport(false)}
                      disabled={loading}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-300 hover:bg-gray-400 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkImport}
                      disabled={loading}
                      className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" role="status" aria-label="Loading"></div>
                          Importing...
                        </>
                      ) : 'Import'}
                    </button>
                  </div>
                  </div>
                </motion.div>
              </div>
              </AnimatePresence>
            , document.body)}

            {/* Users Table with Performance Improvements */}
            <PaginatedTable
              data={users || []}
              columns={[
                {
                  key: 'userId',
                  header: 'User',
                  sortable: true,
                  render: (user: User) => (
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.userId}</div>
                      <div className="text-sm text-gray-500">Created: {new Date(user.createdAt).toLocaleDateString()}</div>
                    </div>
                  )
                },
                {
                  key: 'role',
                  header: 'Role',
                  sortable: true,
                  render: (user: User) => (
                    editingUser?.userId === user.userId ? (
                      <select
                        value={editingUser.role}
                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="user">User</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                        {user.role}
                      </span>
                    )
                  )
                },
                {
                  key: 'isEnabled',
                  header: 'Status',
                  sortable: true,
                  render: (user: User) => (
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {user.isEnabled ? 'Active' : 'Disabled'}
                      </span>
                      {user.status === 'FORCE_CHANGE_PASSWORD' && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Password Reset Required
                        </span>
                      )}
                    </div>
                  )
                },
                {
                  key: 'mfaRequired',
                  header: 'MFA',
                  sortable: true,
                  render: (user: User) => (
                    <MFAStatusBadge
                      mfaRequired={user.mfaRequired || false}
                      mfaSetupComplete={user.mfaSetupComplete || false}
                      size="sm"
                    />
                  )
                },
                {
                  key: 'monthlyDataUsage',
                  header: 'Data Usage',
                  sortable: true,
                  render: (user: User) => {
                    const totalBytes = user.monthlyDataUsage || 0;
                    const downloadBytes = user.monthlyDownloadSize || 0;
                    const uploadBytes = user.monthlyUploadSize || 0;
                    const formatSize = (bytes: number) => {
                      if (bytes >= 1024 * 1024 * 1024) {
                        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
                      }
                      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
                    };
                    return (
                      <button
                        onClick={() => setShowDataUsageModal(user.userId)}
                        className="text-left hover:bg-blue-50 rounded-lg p-2 -m-2 transition-colors group"
                        title="Click to view detailed usage by bucket"
                      >
                        <div className="flex items-center space-x-1">
                          <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600">{formatSize(totalBytes)}</div>
                          <svg className="w-3 h-3 text-gray-400 group-hover:text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap">
                          <span className="text-green-600">↓{formatSize(downloadBytes)}</span>
                          {' / '}
                          <span className="text-blue-600">↑{formatSize(uploadBytes)}</span>
                        </div>
                      </button>
                    );
                  }
                }
              ]}
              loading={usersLoading}
              pageSize={15}
              searchPlaceholder="Search users by ID, role, status..."
              emptyMessage="No users found"
              actions={(user: User) => (
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-2">
                    {editingUser?.userId === user.userId ? (
                      <>
                        <button
                          onClick={() => updateUserRole(user.userId, editingUser.role)}
                          className="text-green-600 hover:text-green-900 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingUser(user)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => toggleUserStatus(user.userId, user.isEnabled)}
                          className={`text-sm font-medium ${user.isEnabled ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                        >
                          {user.isEnabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => forcePasswordReset(user.userId)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Reset Password
                        </button>
                        <button
                          onClick={() => loadActivityLogs(user.userId)}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          View Logs
                        </button>
                        <button
                          onClick={() => setDeletingUser(user)}
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                  {!editingUser && (
                    <AdminMFAControls
                      userId={user.userId}
                      mfaRequired={user.mfaRequired || false}
                      mfaSetupComplete={user.mfaSetupComplete || false}
                      onMFAChange={refreshUsers}
                    />
                  )}
                </div>
              )}
            />


          </div>
        )}

        {/* Bucket Management Tab */}
        {activeTab === 'buckets' && <BucketManagement />}

        {/* Permission Management Tab */}
        {activeTab === 'permissions' && <PermissionManagement />}

        {/* MFA Management Tab */}
        {activeTab === 'mfa' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Multi-Factor Authentication Management</h2>
            
            {/* MFA Statistics */}
            <MFAStatisticsWidget />
            
            {/* MFA Users Table */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Users with MFA Status</h3>
                <p className="text-sm text-gray-500">
                  Select users to require or remove MFA in bulk
                </p>
              </div>

              {/* Bulk action bar - only visible once something is selected */}
              {selectedMfaUsers.size > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedMfaUsers.size} user{selectedMfaUsers.size !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => setSelectedMfaUsers(new Set())}
                      disabled={mfaBulkLoading}
                      className="text-sm text-blue-700 hover:text-blue-900 underline disabled:opacity-50"
                    >
                      Clear selection
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleBulkMFA(true)}
                      disabled={mfaBulkLoading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      {mfaBulkLoading ? 'Updating...' : 'Require MFA'}
                    </button>
                    <button
                      onClick={() => handleBulkMFA(false)}
                      disabled={mfaBulkLoading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                      {mfaBulkLoading ? 'Updating...' : "Don't Require MFA"}
                    </button>
                  </div>
                </div>
              )}

              <PaginatedTable
                selectable
                rowKey={(user: User) => user.userId}
                selectedKeys={selectedMfaUsers}
                onSelectionChange={setSelectedMfaUsers}
                data={users || []}
                columns={[
                  {
                    key: 'userId',
                    header: 'User',
                    sortable: true,
                    render: (user: User) => (
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.userId}</div>
                        <div className="text-sm text-gray-500">
                          {user.mfaSetupDate && `MFA since: ${new Date(user.mfaSetupDate).toLocaleDateString()}`}
                        </div>
                      </div>
                    )
                  },
                  {
                    key: 'role',
                    header: 'Role',
                    sortable: true,
                    render: (user: User) => (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.role}
                      </span>
                    )
                  },
                  {
                    key: 'mfaRequired',
                    header: 'MFA Status',
                    sortable: true,
                    render: (user: User) => (
                      <MFAStatusBadge
                        mfaRequired={user.mfaRequired || false}
                        mfaSetupComplete={user.mfaSetupComplete || false}
                        size="md"
                      />
                    )
                  },
                  {
                    key: 'mfaLastUsed',
                    header: 'Last Used',
                    sortable: true,
                    render: (user: User) => (
                      <span className="text-sm text-gray-900">
                        {user.mfaLastUsed ? new Date(user.mfaLastUsed).toLocaleString() : 'Never'}
                      </span>
                    )
                  },
                  {
                    key: 'mfaRequiredBy',
                    header: 'Required By',
                    sortable: true,
                    render: (user: User) => (
                      <span className="text-sm text-gray-600">
                        {user.mfaRequiredBy || '-'}
                      </span>
                    )
                  }
                ]}
                loading={usersLoading}
                pageSize={15}
                searchPlaceholder="Search users..."
                emptyMessage="No users found"
                actions={(user: User) => (
                  <AdminMFAControls
                    userId={user.userId}
                    mfaRequired={user.mfaRequired || false}
                    mfaSetupComplete={user.mfaSetupComplete || false}
                    onMFAChange={refreshUsers}
                  />
                )}
              />
            </div>
          </div>
        )}

        {/* Password Policy Tab */}
        {activeTab === 'password-policy' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Password Policy</h2>
            <PasswordPolicySettings />
          </div>
        )}

        {/* Bucket Templates Tab */}
        {activeTab === 'templates' && <BucketTemplateManager />}

        {/* External Buckets Tab */}
        {activeTab === 'external' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">External S3 Buckets</h2>
              <button
                onClick={refreshExternalBuckets}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>

            {/* Buckets Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{bucketsSummary.total}</div>
                <div className="text-sm text-blue-600">Total Buckets</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{bucketsSummary.available}</div>
                <div className="text-sm text-green-600">Available to Import</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">{bucketsSummary.managed}</div>
                <div className="text-sm text-gray-600">Already Managed</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{bucketsSummary.filtered}</div>
                <div className="text-sm text-yellow-600">AWS Managed (Hidden)</div>
              </div>
            </div>

            {bucketsSummary.filtered > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>{bucketsSummary.filtered} AWS managed buckets</strong> are hidden from this list, including SAM CLI deployment buckets, CloudFormation buckets, and other AWS service buckets.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <PaginatedTable
              data={externalBuckets || []}
              columns={[
                {
                  key: 'Name',
                  header: 'Bucket Name',
                  sortable: true,
                  render: (bucket: any) => (
                    <span className="text-sm font-medium text-gray-900">{bucket.Name}</span>
                  )
                },
                {
                  key: 'CreationDate',
                  header: 'Creation Date',
                  sortable: true,
                  render: (bucket: any) => (
                    <span className="text-sm text-gray-500">
                      {new Date(bucket.CreationDate).toLocaleDateString()}
                    </span>
                  )
                },
                {
                  key: 'status',
                  header: 'Status',
                  sortable: true,
                  render: (bucket: any) => (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      bucket.status === 'managed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {bucket.status === 'managed' ? 'Managed' : 'Available'}
                    </span>
                  )
                }
              ]}
              loading={externalBucketsLoading}
              pageSize={10}
              searchPlaceholder="Search external buckets..."
              emptyMessage={
                externalBuckets.length === 0 && bucketsSummary.managed > 0
                  ? 'All discovered buckets are already managed by the portal.'
                  : 'No external buckets found'
              }
              actions={(bucket: any) => (
                bucket.status !== 'managed' ? (
                  <button
                    onClick={() => importExternalBucket(bucket.Name)}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    Import to Portal
                  </button>
                ) : null
              )}
            />
          </div>
        )}

        {/* Data Retention Tab */}
        {activeTab === 'retention' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Data Retention Settings</h2>
            <RetentionSettings />
          </div>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Branding</h2>
            <BrandingSettings />
          </div>
        )}

        {/* Audit Trail Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Admin Audit Trail</h2>
            <PaginatedTable
              data={auditTrail || []}
              columns={[
                {
                  key: 'adminId',
                  header: 'Admin',
                  sortable: true,
                  render: (entry: any) => (
                    <span className="text-sm font-medium text-gray-900">{entry.adminId}</span>
                  )
                },
                {
                  key: 'action',
                  header: 'Action',
                  sortable: true,
                  render: (entry: any) => (
                    <span className="text-sm text-gray-900">{entry.action}</span>
                  )
                },
                {
                  key: 'details',
                  header: 'Details',
                  render: (entry: any) => (
                    <div className="text-sm text-gray-500 max-w-xs">
                      <div className="max-w-xs overflow-hidden break-words whitespace-pre-wrap text-xs" title={JSON.stringify(entry.details, null, 2)}>
                        {JSON.stringify(entry.details, null, 2)}
                      </div>
                    </div>
                  )
                },
                {
                  key: 'timestamp',
                  header: 'Timestamp',
                  sortable: true,
                  render: (entry: any) => (
                    <span className="text-sm text-gray-500">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  )
                }
              ]}
              loading={auditTrailLoading}
              pageSize={20}
              searchPlaceholder="Search audit trail by admin, action..."
              emptyMessage="No audit trail entries found"
              defaultSort={{ key: 'timestamp', direction: 'desc' }}
            />
          </div>
        )}

        {/* User Delete Confirmation */}
        <UserDeleteConfirmation
          user={deletingUser}
          onConfirm={handleDeleteUser}
          onCancel={() => setDeletingUser(null)}
          isOpen={!!deletingUser}
        />

        {/* Activity Logs Modal */}
        <ActivityLogsModal
          isOpen={!!showActivityLogs}
          onClose={() => {
            setShowActivityLogs(null);
            setActivityLogs([]);
          }}
          userId={showActivityLogs || ''}
          activityLogs={activityLogs}
          loading={activityLogsLoading}
        />

        {/* User Data Usage Modal */}
        <UserDataUsageModal
          isOpen={!!showDataUsageModal}
          onClose={() => setShowDataUsageModal(null)}
          userId={showDataUsageModal || ''}
        />
    </div>
  );
};

export default EnhancedAdminDashboard;